import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { FormField } from '../../components/Formulaire/FormField';
import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import CreatableSelect from 'react-select/creatable';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';

const EmployeeForm = ({ refreshEmployee, onSuccess }) => {
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor,
            boxShadow: state.isFocused ? '0 0 0 1px var(--color-primary)' : provided.boxShadow,
            '&:hover': {
                borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor
            },
            borderRadius: '0.375rem',
            padding: '2px',
            minHeight: '42px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--color-primary)'
                : state.isFocused
                    ? 'rgba(var(--color-primary-rgb), 0.1)'
                    : provided.backgroundColor,
            color: state.isSelected ? 'white' : provided.color
        })
    };
    const { id } = useParams();
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [departmentsOptions, setDepartmentsOptions] = useState([]);
    const [assignmentsOptions, setAssignmentsOptions] = useState([]);
    const [employee, setEmployee] = useState({
        name: '',
        gender: 'male',
        birthday: '',
        email: '',
        poste: '',
        manager: '',
        manager_name: '',
        phone: '',
        address: '',
        maritalStatus: '',
        spouseIncome: false,
        numberOfChildren: 0,
        nationality: '',
        idCardOrPassport: '',
        company_id: 4,
        is_non_resident: false,
        categories: [],
        department: null,
        department_name: '',
        assignment: null,
        assignment_name: ''
    });

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(response => {
                const options = response.data.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }));
                setCategoriesOptions(options);
            })
            .catch(error => {
                console.error('Erreur lors du chargement des catégories', error);
            });

        // load departments
        apiService.getElement('/employee/departments')
            .then(response => {
                const opts = response.data.departments.map(d => ({ value: d.id, label: d.name }));
                setDepartmentsOptions(opts);
            })
            .catch(err => {
                console.error('Erreur chargement départements', err);
            });

        // load assignments
        apiService.getElement('/employee/assignments')
            .then(response => {
                const opts = response.data.assignments.map(a => ({ value: a.id, label: a.name }));
                setAssignmentsOptions(opts);
            })
            .catch(err => {
                console.error('Erreur chargement assignments', err);
            });
    }, []);

    useEffect(() => {
        if (id) {
            setErrorMessage('');
            setLoading(true);
            apiService.getElementById('/employee', id).then(response => {
                const employeeData = response.data;
                console.log('Employee data:', employeeData);

                // map potential server fields to local state keys
                const mapped = {
                    ...employee,
                    ...employeeData,
                };
                // handle department coming from API as department_id or department
                if (employeeData.department_id) {
                    mapped.department = employeeData.department_id.id || employeeData.department_id;
                    mapped.department_name = employeeData.department_id.name || mapped.department_name;
                }
                if (employeeData.assignment_id) {
                    mapped.assignment = employeeData.assignment_id.id || employeeData.assignment_id;
                    mapped.assignment_name = employeeData.assignment_id.name || mapped.assignment_name;
                }
                // categories may come as category_ids array
                if (employeeData.category_ids && Array.isArray(employeeData.category_ids)) {
                    mapped.categories = employeeData.category_ids.map(c => (typeof c === 'object' ? { id: c.id, name: c.name } : { id: c, name: '' }));
                }

                setEmployee(mapped);

            }).catch(error => {
                console.error('Error fetching employee:', error);
                notify('Erreur lors de la récupération des données de l\'employé.', '', 3);

            }).finally(() => setLoading(false));
        }
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEmployee({
            ...employee,
            [name]: value
        });
    };

    const handleSelectChange = (selectedOption) => {
        setEmployee({
            ...employee,
            nationality: selectedOption.value
        });
    };

    const handleManagerChange = (selectedOption) => {
        setEmployee({
            ...employee,
            manager: selectedOption ? selectedOption.value : null,
            manager_name: selectedOption ? selectedOption.label : ''
        });
    };

    const handleDepartmentChange = (selectedOption) => {
        setEmployee({
            ...employee,
            department: selectedOption ? selectedOption.value : null,
            department_name: selectedOption ? selectedOption.label : ''
        });
    };

    const handleAssignmentChange = (selectedOption) => {
        setEmployee({
            ...employee,
            assignment: selectedOption ? selectedOption.value : null,
            assignment_name: selectedOption ? selectedOption.label : ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');
        // Prépare le payload pour l'API
        const payload = {
            ...employee,
            category_ids: (employee.categories || []).map(cat => cat.id),
            department_id: employee.department || false,
            assignment_id: employee.assignment || false
        };
        console.log('Payload to send:', payload.category_ids);
        if (id) {
            apiService
                .updateElementById("/employee", id, payload)
                .then(response => {
                    console.log('Employee updated:', response.data);
                    if(response.data?.result?.employee_id) {
                        notify('Employé mis à jour avec succès !', '', 2);
                        refreshEmployee();
                        onSuccess(id);
                        setLoading(false);
                    }
                })
                .catch((error) => {
                    console.log(error)
                    notify('Erreur lors de la mise à jour. Veuillez réessayer.', '', 3);
                    setLoading(false);
                });
        } else {
            apiService
                .saveElement('/employee', payload)
                .then(response => {
                    const newEmployeeId = response?.data?.result?.employee_id
                    if (newEmployeeId) {
                        notify('Employé créé avec succès !', '', 2);
                        onSuccess(response?.data?.result?.employee_id);
                    } else {
                        notify('Erreur lors de la création. Veuillez réessayer.', '', 3);
                    }
                })
                .catch((error) => {
                    console.log('error ', error);
                    notify('Erreur lors de la création. Veuillez réessayer.', '', 3);
                });
            setLoading(false);
        }
    };

    const options = countryList().getData();

    return (
        <>
             
            <form onSubmit={handleSubmit} className="p-6">
                {errorMessage && <div className="text-center text-red-500 mb-4 p-3 bg-red-50 rounded-lg">{errorMessage}</div>}
                <h2 className="text-lg font-bold mb-4 flex items-center">
                    <span className="w-2 h-6 bg-blue-500 rounded-sm mr-2"></span>
                    Informations personnelles
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <FormField label="Nom complet:">
                        <input
                            type="text"
                            name="name"
                            value={employee.name || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                            required
                        />
                    </FormField>

                    <FormField label="Genre:">
                        <select
                            name="gender"
                            value={employee.gender || 'male'}
                            onChange={handleChange}
                            className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                            required
                        >
                            <option value="male">Homme</option>
                            <option value="female">Femme</option>
                        </select>
                    </FormField>

                    <FormField label="Date de naissance:">
                        <input
                            type="date"
                            name="birthday"
                            value={employee.birthday || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>

                    <FormField label="Email:">
                        <input
                            type="email"
                            name="email"
                            value={employee.email || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                    <FormField label="Phone:">
                        <input
                            type="text"
                            name="phone"
                            value={employee.phone || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                    <FormField label="Address:">
                        <input
                            type="text"
                            name="address"
                            value={employee.address || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                    <FormField label="ID Card or Passport:">
                        <input
                            type="text"
                            name="idCardOrPassport"
                            value={employee.idCardOrPassport || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                
                    <FormField label="Nationalité:">
                        <Select
                            name="nationality"
                            value={options.find(option => option.value === employee.nationality) || ''}
                            onChange={handleSelectChange}
                            options={options}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Sélectionnez un pays..."
                            styles={customSelectStyles}
                        />
                    </FormField>
                    <FormField label="">
                            <span className="text-sm text-gray-500">Cochez si l'employé est non resident</span>
                            <input
                                type="checkbox"
                                id="formIsNonResident"
                                name="is_non_resident"
                                checked={employee.is_non_resident}
                                onChange={(e) => setEmployee({ ...employee, is_non_resident: e.target.checked })}
                                className="checkbox"
                            />
                    </FormField>
                </div>

                <h2 className="text-lg font-bold mb-4 flex items-center">
                    <span className="w-2 h-6 bg-orange-500 rounded-sm mr-2"></span>
                    Informations familiales
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <FormField label="Situation Marital:">
                        <select
                            id="formMaritalStatus"
                            name="maritalStatus"
                            value={employee.maritalStatus || 'single'}
                            onChange={(e) => setEmployee({ ...employee, maritalStatus: e.target.value })}
                            className="select select-bordered"
                            required>
                            <option value="single">Célibataire</option>
                            <option value="married">Marié(e)</option>
                            <option value="widower">Veuf(ve)</option>
                            <option value="divorced">Divorcé(e)</option>
                        </select>
                    </FormField>

                    {employee.maritalStatus === 'married' && (
                        <FormField label="Conjoint avec revenu:">
                            <select
                                id="formSpouseIncome"
                                name="spouseIncome"
                                value={employee.spouseIncome === true ? "yes" : "no"}
                                onChange={(e) => setEmployee({ ...employee, spouseIncome: e.target.value === "yes" })}
                                className="select select-bordered"
                                required>
                                <option value="no">Non</option>
                                <option value="yes">Oui</option>
                            </select>
                        </FormField>
                    )}
                    <FormField label="Number of Children:">
                        <input
                            type="number"
                            name="numberOfChildren"
                            value={employee.numberOfChildren || 0}
                            onChange={handleChange}
                            className="input input-bordered"
                        />
                    </FormField>
                </div>

                <h2 className="text-lg font-bold mb-4">Company Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <FormField label="Poste:">
                        <input
                            type="text"
                            name="poste"
                            value={employee.poste || ''}
                            onChange={handleChange}
                            className="input input-bordered"
                        />
                    </FormField>
                    <FormField label="Manager:">
                        <EmployeeSelect
                            value={employee.manager && employee.manager_name ? { value: employee.manager, label: employee.manager_name } : null}
                            onChange={handleManagerChange}
                            placeholder="Choisir un manager"
                            nameKey="label"
                            isClearable
                        />
                    </FormField>

                    <FormField label="Département:">
                        <CreatableSelect
                            value={departmentsOptions.find(opt => opt.value === employee.department) || null}
                            onChange={(opt) => handleDepartmentChange(opt)}
                            options={departmentsOptions}
                            styles={customSelectStyles}
                            placeholder="Sélectionner ou créer..."
                            isClearable
                            onCreateOption={async (inputValue) => {
                                try {
                                    const response = await apiService.saveElement('/employee/department', { name: inputValue });
                                    const newId = response?.data?.result?.id || response?.data?.id;
                                    const newName = response?.data?.result?.name || response?.data?.name;
                                    if (!newId) throw new Error('Création du département a échoué');
                                    const newOpt = { value: newId, label: newName };
                                    setDepartmentsOptions(prev => [...prev, newOpt]);
                                    setEmployee(prev => ({ ...prev, department: newId, department_name: newName }));
                                } catch (err) {
                                    console.error('Erreur création département:', err);
                                    notify('Erreur lors de la création du département.', '', 3);
                                }
                            }}
                        />
                    </FormField>

                    <FormField label="Affectation:">
                        <CreatableSelect
                            value={assignmentsOptions.find(opt => opt.value === employee.assignment) || null}
                            onChange={(opt) => handleAssignmentChange(opt)}
                            options={assignmentsOptions}
                            styles={customSelectStyles}
                            placeholder="Sélectionner ou créer..."
                            isClearable
                            onCreateOption={async (inputValue) => {
                                try {
                                    const response = await apiService.saveElement('/employee/assignment', { name: inputValue });
                                    const newId = response?.data?.result?.id || response?.data?.id;
                                    const newName = response?.data?.result?.name || response?.data?.name;
                                    if (!newId) throw new Error('Création de l\'assignment a échoué');
                                    const newOpt = { value: newId, label: newName };
                                    setAssignmentsOptions(prev => [...prev, newOpt]);
                                    setEmployee(prev => ({ ...prev, assignment: newId, assignment_name: newName }));
                                } catch (err) {
                                    console.error('Erreur création assignment:', err);
                                    notify('Erreur lors de la création de l\'assignment.', '', 3);
                                }
                            }}
                        />
                    </FormField>
                </div>
                <FormField label="Etiquettes:">
                    <CreatableSelect
                        name="categories"
                        value={categoriesOptions.filter(opt => (employee.categories || []).some(cat => cat.id === opt.value))}
                        onChange={selected =>
                            setEmployee({
                                ...employee,
                                categories: selected ? selected.map(opt => ({ id: opt.value, name: opt.label })) : []
                            })
                        }
                        onCreateOption={async (inputValue) => {
                            // Appel API pour créer la catégorie
                            try {
                                const response = await apiService.saveElement('/employee/category', { name: inputValue });
                                console.log('New category created:', response);
                                const newCat = { value: response.data.result.id, label: response.data.result.name };
                                setCategoriesOptions(prev => [...prev, newCat]);
                                setEmployee({
                                    ...employee,
                                    categories: [...(employee.categories || []), { id: newCat.value, name: newCat.label }]
                                });
                            } catch (err) {
                                notify('Erreur lors de la création de la catégorie.', '', 3);
                            }
                        }}
                        options={categoriesOptions}
                        isMulti
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Sélectionnez ou créez une etiquette..."
                        styles={customSelectStyles}
                    />
                </FormField>
                <div className="flex justify-center mt-8 gap-4">
                    <button type="submit" className="bg-[--color-primary] hover:bg-[--bg-hover] rounded-full text-white px-12 py-3 font-medium transition-all duration-200 shadow-sm">
                        {id ? 'Mettre à jour l\'employé' : 'Créer un employé'}
                    </button>
                    {loading && (
                        <div className="flex justify-center">
                            <div className="loader">
                                <span className="loading loading-bars loading-xl text-[--color-primary]"></span>
                            </div>
                        </div>
                    )}
                </div>
            </form>
            
        </>
    );
};

export default EmployeeForm;